/**
 * useChainProvider Hook
 * React hook for accessing JsonRpcProviders for supported chains
 */

import { useState, useEffect, useCallback } from 'react'
import { JsonRpcProvider, Contract, formatUnits } from 'ethers'
import { getChainProvider, getChainConfig } from '@/lib/chainProvider'
import { ChainPriceConfig } from '@/lib/tokenPriceConfig'
import { isPageVisible } from '@/lib/pageVisibility'

export interface UseChainProviderResult {
  provider: JsonRpcProvider | null
  loading: boolean
  error: string | null
  chainConfig: ChainPriceConfig | null
}

/**
 * Get a provider for a specific chain
 */
export function useChainProvider(chainId: number): UseChainProviderResult {
  const [provider, setProvider] = useState<JsonRpcProvider | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchProvider() {
      setLoading(true)
      setError(null)

      try {
        const p = await getChainProvider(chainId)
        if (!cancelled) {
          setProvider(p)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to connect to chain')
          setProvider(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchProvider()

    return () => {
      cancelled = true
    }
  }, [chainId])

  return {
    provider,
    loading,
    error,
    chainConfig: getChainConfig(chainId),
  }
}

// ERC20 balance ABI
const ERC20_BALANCE_ABI = ['function balanceOf(address owner) view returns (uint256)']

export interface UseTokenBalanceResult {
  balance: bigint
  formatted: string
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Get token balance on a specific chain using public RPC
 */
export function useTokenBalance(
  chainId: number,
  tokenAddress: string | null,
  userAddress: string | null,
  decimals: number = 18
): UseTokenBalanceResult {
  const [balance, setBalance] = useState<bigint>(0n)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function fetchBalance() {
      if (!isPageVisible()) return
      if (!tokenAddress || !userAddress) {
        setBalance(0n)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const provider = await getChainProvider(chainId)
        const contract = new Contract(tokenAddress, ERC20_BALANCE_ABI, provider)
        const bal = await contract.balanceOf(userAddress)

        if (!cancelled) {
          setBalance(bal)
        }
      } catch (err) {
        if (!cancelled) {
          console.error(`[useTokenBalance] Failed to fetch balance on chain ${chainId}:`, err)
          setError(err instanceof Error ? err.message : 'Failed to fetch balance')
          setBalance(0n)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchBalance()

    // Auto-refresh every 15 seconds
    const interval = setInterval(fetchBalance, 15000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [chainId, tokenAddress, userAddress, refetchTrigger])

  return {
    balance,
    formatted: formatUnits(balance, decimals),
    loading,
    error,
    refetch,
  }
}

export interface UseMultiChainBalancesResult {
  balances: Record<number, bigint>
  loading: boolean
  refetch: () => void
}

/**
 * Get token balances across multiple chains
 */
export function useMultiChainBalances(
  chainIds: number[],
  getTokenAddress: (chainId: number) => string | null,
  userAddress: string | null
): UseMultiChainBalancesResult {
  const [balances, setBalances] = useState<Record<number, bigint>>({})
  const [loading, setLoading] = useState(false)
  const [refetchTrigger, setRefetchTrigger] = useState(0)

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function fetchAllBalances() {
      if (!isPageVisible()) return
      if (!userAddress) {
        setBalances({})
        return
      }

      setLoading(true)

      const newBalances: Record<number, bigint> = {}

      await Promise.all(
        chainIds.map(async (chainId) => {
          const tokenAddress = getTokenAddress(chainId)
          if (!tokenAddress) return

          try {
            const provider = await getChainProvider(chainId)
            const contract = new Contract(tokenAddress, ERC20_BALANCE_ABI, provider)
            const bal = await contract.balanceOf(userAddress)
            newBalances[chainId] = bal
          } catch (err) {
            console.error(`[useMultiChainBalances] Failed to fetch balance on chain ${chainId}:`, err)
            newBalances[chainId] = 0n
          }
        })
      )

      if (!cancelled) {
        setBalances(newBalances)
        setLoading(false)
      }
    }

    fetchAllBalances()

    // Auto-refresh every 15 seconds
    const interval = setInterval(fetchAllBalances, 15000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [chainIds, getTokenAddress, userAddress, refetchTrigger])

  return {
    balances,
    loading,
    refetch,
  }
}
